import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../lib/authFetch';
import { useAuth, useAppActive } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Card, CardHeader, CardTitle, CardContent,
  Button, Input, Select, Modal, ModalFooter, Badge, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
  LastUpdated,
} from '../components/ui';
import { formatCurrency, formatDate, formatNumber, formatRelativeTime } from '../lib/utils';
import { 
  Plus, Trash2, RefreshCw, Edit, Save, Database, LogIn,
  ChevronLeft, ChevronRight, Check, AlertCircle, Loader2, X, Bug, Settings, BarChart3, ShoppingCart, Megaphone
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonAdPlatform } from '../components/ui/Skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
];

const getMonthLabel = (date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const getMonthRange = (year, month) => ({
  start: new Date(year, month, 1).toISOString().split('T')[0],
  end: new Date(year, month + 1, 0).toISOString().split('T')[0]
});


export default function MetaAds() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [fetchingAccount, setFetchingAccount] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showCampaignsModal, setShowCampaignsModal] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Debug state
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  
  // Modal step state (1 = basic info, 2 = API connection)
  const [modalStep, setModalStep] = useState(1);
  const [savedAccountId, setSavedAccountId] = useState(null);
  const savedAccountIdRef = useRef(null); // Ref for immediate access
  
  // Form state
  const [formData, setFormData] = useState({
    accountName: '',
    accountId: '',
    accessToken: '',
    currency: 'USD',
    exchangeRate: '1',
    taxRate: '0',
    showOnDashboard: true,
  });
  
  // API connection state for modal
  const [connecting, setConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [loadedCampaigns, setLoadedCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState({});
  
  const [manualForm, setManualForm] = useState({
    accountId: '',
    date: new Date().toISOString().split('T')[0],
    spend: '',
    impressions: '',
    clicks: '',
  });

  // Handle visibility change - reset stuck states when tab becomes visible
  const isLoadingRef = useRef(false);
  
  useEffect(() => {
    let lastHiddenTime = Date.now();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceHidden = Date.now() - lastHiddenTime;
        
        // If we've been hidden for more than 30 seconds, reset stuck states
        if (timeSinceHidden > 30000) {
          setFetchingAll(false);
          setFetchingAccount(null);
          setConnecting(false);
          setDebugLoading(false);
          setSaving(false);
        }
      } else {
        lastHiddenTime = Date.now();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const getViewMonth = useCallback(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() - monthOffset, 1);
  }, [monthOffset]);

  // Silent reload (doesn't show loading spinner)
  const loadDataSilent = useCallback(async () => {
    if (!user || isLoadingRef.current) return;
    
    try {
      const { data: accountsData, error: accErr } = await supabase
        .from('meta_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      // Ignore abort errors
      if (accErr && !accErr.message?.includes('AbortError') && !accErr.message?.includes('Lock')) {
        console.error('Accounts error:', accErr);
      }
      if (accountsData) setAccounts(accountsData);

      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data: daily, error: dailyErr } = await supabase
        .from('meta_daily')
        .select('*, meta_accounts(account_name)')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: false });
      
      if (dailyErr && !dailyErr.message?.includes('AbortError') && !dailyErr.message?.includes('Lock')) {
        console.error('Daily error:', dailyErr);
      }
      if (daily) setDailyData(daily);

      const { data: campaignsData, error: campErr } = await supabase
        .from('meta_campaigns')
        .select('*')
        .eq('user_id', user.id);
      
      if (campErr && !campErr.message?.includes('AbortError') && !campErr.message?.includes('Lock')) {
        console.error('Campaigns error:', campErr);
      }
      if (campaignsData) setCampaigns(campaignsData);
      
    } catch (e) {
      // Silently ignore abort/lock errors
      if (!e.message?.includes('AbortError') && !e.message?.includes('Lock')) {
        console.error('Load error:', e);
      }
    }
  }, [user, getViewMonth]);
  
  // Use global app active hook to refresh data when returning to tab
  useAppActive(useCallback(() => {
    if (!isLoadingRef.current) {
      loadDataSilent();
    }
  }, [loadDataSilent]));

  const loadData = useCallback(async () => {
    if (!user) return;
    
    isLoadingRef.current = true;
    setLoading(true);
    
    try {
      const { data: accountsData, error: accErr } = await supabase
        .from('meta_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      // Ignore abort/lock errors
      if (accErr && !accErr.message?.includes('AbortError') && !accErr.message?.includes('Lock')) {
        console.error('Accounts error:', accErr);
      }
      setAccounts(accountsData || []);

      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data: daily, error: dailyErr } = await supabase
        .from('meta_daily')
        .select('*, meta_accounts(account_name)')
        .eq('user_id', user.id)
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: false });
      
      if (dailyErr && !dailyErr.message?.includes('AbortError') && !dailyErr.message?.includes('Lock')) {
        console.error('Daily error:', dailyErr);
      }
      setDailyData(daily || []);

      const { data: campaignsData, error: campErr } = await supabase
        .from('meta_campaigns')
        .select('*')
        .eq('user_id', user.id);
      
      if (campErr && !campErr.message?.includes('AbortError') && !campErr.message?.includes('Lock')) {
        console.error('Campaigns error:', campErr);
      }
      setCampaigns(campaignsData || []);
      
    } catch (e) {
      // Don't show error toast for abort/lock errors
      if (!e.message?.includes('AbortError') && !e.message?.includes('Lock')) {
        console.error('Load error:', e);
        toast.error('Failed to load data');
      }
    }
    
    setLoading(false);
    isLoadingRef.current = false;
  }, [user, getViewMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormData({
      accountName: '',
      accountId: '',
      accessToken: '',
      currency: 'USD',
      exchangeRate: '1',
      taxRate: '0',
      showOnDashboard: true,
    });
    setModalStep(1);
    setSavedAccountId(null);
    savedAccountIdRef.current = null; // Clear ref too
    setIsConnected(false);
    setConnectionError(null);
    setLoadedCampaigns([]);
    setSelectedCampaigns({});
  };

  // Debug API call
  const handleDebug = async (account) => {
    if (!account?.account_id || !account?.access_token) {
      toast.error('No API credentials');
      return;
    }
    
    setSelectedAccount(account);
    setDebugLoading(true);
    setDebugData(null);
    setShowDebugModal(true);
    
    try {
      const res = await authFetch('/api/debug-meta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId: account.account_id,
          accessToken: account.access_token,
        })
      });
      
      const data = await res.json();
      console.log('Debug response:', data);
      setDebugData(data);
    } catch (e) {
      setDebugData({ error: e.message });
    }
    
    setDebugLoading(false);
  };

  // Connect to Meta API and load campaigns
  const handleConnect = async () => {
    if (!formData.accountId.trim() || !formData.accessToken.trim()) {
      toast.error('Account ID and Access Token required');
      return;
    }

    setConnecting(true);
    setConnectionError(null);
    
    try {
      console.log('Connecting with:', { 
        accountId: formData.accountId, 
        tokenLength: formData.accessToken.length 
      });
      
      const res = await authFetch('/api/fetch-meta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'connect',
          accountId: formData.accountId.trim(),
          accessToken: formData.accessToken.trim(),
        })
      });
      
      const data = await res.json();
      console.log('Connect response:', data);
      
      if (!data.success) {
        const errMsg = data.error || data.message || 'Failed to connect';
        setConnectionError(errMsg);
        toast.error(errMsg);
        setConnecting(false);
        return;
      }
      
      // Load campaigns
      const camps = data.campaigns || [];
      console.log('Campaigns loaded:', camps);
      setLoadedCampaigns(camps);
      
      // Default all campaigns to included
      const selection = {};
      camps.forEach(c => { selection[c.id] = true; });
      setSelectedCampaigns(selection);
      
      setIsConnected(true);
      setConnectionError(null);
      toast.success(`Connected! Found ${camps.length} campaign(s)`);
      
    } catch (e) {
      console.error('Connection error:', e);
      const errMsg = e.message || 'Network error';
      setConnectionError(errMsg);
      toast.error('Connection failed: ' + errMsg);
    }
    
    setConnecting(false);
  };

  // Toggle campaign selection
  const toggleCampaign = (campaignId) => {
    setSelectedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  // Step 1: Save basic account info
  const handleAddAccount = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) return;
    
    if (!formData.accountName.trim()) {
      toast.error('Account name is required');
      return;
    }
    
    if (!formData.accountId.trim()) {
      toast.error('Account ID is required');
      return;
    }

    setSaving(true);
    
    try {
      const insertData = {
        user_id: user.id,
        account_name: formData.accountName.trim(),
        account_id: formData.accountId.trim(),
        access_token: null,
        currency: formData.currency,
        exchange_rate: parseFloat(formData.exchangeRate) || 1.0,
        tax_rate: parseFloat(formData.taxRate) || 0,
        api_enabled: false,
        show_on_dashboard: formData.showOnDashboard,
        is_connected: false,
      };
      
      console.log('Inserting account:', insertData);
      
      const { data: newAccount, error } = await supabase
        .from('meta_accounts')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        if (error.code === '23505') {
          toast.error('This account ID already exists');
        } else {
          toast.error(error.message || 'Failed to add account');
        }
        setSaving(false);
        return;
      }

      console.log('Account created:', newAccount);
      
      // Store ID in both state and ref (ref is immediately available)
      savedAccountIdRef.current = newAccount.id;
      setSavedAccountId(newAccount.id);
      
      toast.success('Account added! You can now optionally connect the API.');
      setSaving(false);
      setModalStep(2); // Move to step 2 for optional API connection
      // Don't call loadData() here - it can interfere with Step 2
    } catch (error) {
      console.error('Error adding account:', error);
      toast.error(error.message || 'Failed to add account');
      setSaving(false);
    }
  };
  
  // Step 2: Connect API (optional)
  const handleConnectApi = async () => {
    // Use ref for immediate access (state might not be updated yet due to React batching)
    const accountId = savedAccountIdRef.current || savedAccountId;
    
    if (!accountId) {
      console.error('No savedAccountId available');
      toast.error('Account ID not found. Please try again.');
      return;
    }
    if (saving) return;
    
    if (!formData.accessToken.trim()) {
      toast.error('Access token is required');
      return;
    }
    
    if (!isConnected) {
      toast.error('Please connect to verify your token first');
      return;
    }

    setSaving(true);
    console.log('handleConnectApi: starting save for account:', accountId);
    
    try {
      console.log('handleConnectApi: updating account...');
      const { error } = await supabase
        .from('meta_accounts')
        .update({
          access_token: formData.accessToken.trim(),
          api_enabled: true,
          is_connected: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', accountId);

      console.log('handleConnectApi: account update done, error:', error);
      if (error) throw error;

      // Save campaigns in batches of 20 to avoid timeout
      if (loadedCampaigns.length > 0) {
        console.log('handleConnectApi: saving', loadedCampaigns.length, 'campaigns...');
        const campaignRecords = loadedCampaigns.map(c => ({
          user_id: user.id,
          meta_account_id: accountId,
          campaign_id: c.id,
          campaign_name: c.name,
          status: c.status,
          excluded: !selectedCampaigns[c.id],
        }));
        
        const BATCH_SIZE = 20;
        for (let i = 0; i < campaignRecords.length; i += BATCH_SIZE) {
          const batch = campaignRecords.slice(i, i + BATCH_SIZE);
          console.log('handleConnectApi: batch', i / BATCH_SIZE + 1);
          const { error: batchErr } = await supabase
            .from('meta_campaigns')
            .upsert(batch, { onConflict: 'user_id,meta_account_id,campaign_id' });
          
          if (batchErr) {
            console.error('Campaign batch error:', batchErr);
            // Continue with other batches even if one fails
          }
        }
        console.log('handleConnectApi: all batches done');
      }

      // Update onboarding progress
      console.log('handleConnectApi: updating onboarding...');
      await supabase
        .from('onboarding_progress')
        .update({ meta_ads_connected: true, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      
      console.log('handleConnectApi: success!');
      toast.success('API connected successfully!');
      setSaving(false);
      setShowAddModal(false);
      resetForm();
      await loadData();
    } catch (error) {
      console.error('Error connecting API:', error);
      toast.error(error.message || 'Failed to connect API');
      setSaving(false);
    }
  };

  // Edit account
  const handleEditAccount = async (e) => {
    e.preventDefault();
    if (!selectedAccount) return;
    
    // Prevent double submission
    if (saving) return;
    
    if (!formData.accountName.trim()) {
      toast.error('Account name is required');
      return;
    }
    
    if (!formData.accountId.trim()) {
      toast.error('Account ID is required');
      return;
    }

    // If user is trying to connect/update API, verify connection first
    const hasNewApiCredentials = formData.accessToken && 
      formData.accessToken !== selectedAccount.access_token;
    
    if (hasNewApiCredentials && !isConnected) {
      toast.error('Please connect to verify your API token first');
      return;
    }

    setSaving(true);
    
    try {
      const updateData = {
        account_name: formData.accountName.trim(),
        account_id: formData.accountId.trim(),
        currency: formData.currency,
        exchange_rate: parseFloat(formData.exchangeRate) || 1.0,
        tax_rate: parseFloat(formData.taxRate) || 0,
        show_on_dashboard: formData.showOnDashboard,
        updated_at: new Date().toISOString(),
      };
      
      // Only update API fields if user has connected
      if (hasNewApiCredentials && isConnected) {
        updateData.access_token = formData.accessToken.trim();
        updateData.api_enabled = true;
        updateData.is_connected = true;
      } else if (formData.accessToken === '' && selectedAccount.access_token) {
        // User cleared the token - disconnect API
        updateData.access_token = null;
        updateData.api_enabled = false;
        updateData.is_connected = false;
      }
      
      console.log('Updating account:', updateData);
      console.log('Selected account ID:', selectedAccount.id);
      
      console.log('Starting Supabase update...');
      const result = await supabase
        .from('meta_accounts')
        .update(updateData)
        .eq('id', selectedAccount.id)
        .select();
      
      console.log('Supabase update result:', result);
      
      const { error } = result;

      console.log('Account update done, error:', error);

      if (error) {
        console.error('Update error:', error);
        throw error;
      }

      // Update campaigns in batches if we have new API data
      if (isConnected && loadedCampaigns.length > 0) {
        console.log('Saving', loadedCampaigns.length, 'campaigns in batches...');
        const campaignRecords = loadedCampaigns.map(c => ({
          user_id: user.id,
          meta_account_id: selectedAccount.id,
          campaign_id: c.id,
          campaign_name: c.name,
          status: c.status,
          excluded: !selectedCampaigns[c.id],
        }));
        
        const BATCH_SIZE = 20;
        for (let i = 0; i < campaignRecords.length; i += BATCH_SIZE) {
          const batch = campaignRecords.slice(i, i + BATCH_SIZE);
          console.log('Upserting batch', i / BATCH_SIZE + 1, 'of', Math.ceil(campaignRecords.length / BATCH_SIZE));
          const { error: batchErr } = await supabase
            .from('meta_campaigns')
            .upsert(batch, { onConflict: 'user_id,meta_account_id,campaign_id' });
          
          if (batchErr) {
            console.error('Campaign batch error:', batchErr);
          }
        }
        console.log('All campaign batches done');
      }

      console.log('Finishing up...');
      toast.success('Account updated');
      setSaving(false);
      setShowEditModal(false);
      setSelectedAccount(null);
      resetForm();
      console.log('Calling loadData...');
      await loadData();
      console.log('Done!');
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error('Failed to update: ' + error.message);
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    if (saving) return;

    setSaving(true);
    try {
      await supabase.from('meta_campaigns').delete().eq('meta_account_id', selectedAccount.id);
      await supabase.from('meta_daily').delete().eq('meta_account_id', selectedAccount.id);
      
      const { error } = await supabase
        .from('meta_accounts')
        .delete()
        .eq('id', selectedAccount.id);

      if (error) throw error;

      toast.success('Account deleted');
      setSaving(false);
      setShowDeleteModal(false);
      setSelectedAccount(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
      setSaving(false);
    }
  };

  const handleFetchAccount = async (account) => {
    if (!account.is_connected || !account.access_token) {
      toast.error('API not configured for this account');
      return;
    }
    
    setFetchingAccount(account.id);
    const tid = toast.loading(`Fetching ${account.account_name}...`);
    
    try {
      const accountCampaigns = campaigns.filter(c => c.meta_account_id === account.id);
      const excludedIds = accountCampaigns.filter(c => c.excluded).map(c => c.campaign_id);
      
      const requestBody = {
        action: 'fetch',
        userId: user.id,
        metaAccountId: account.id,
        accountId: account.account_id,
        accessToken: account.access_token,
        exchangeRate: account.exchange_rate || 1,
        taxRate: account.tax_rate || 0,
        excludedCampaigns: excludedIds,
      };
      
      console.log('Fetch request:', { ...requestBody, accessToken: '[hidden]' });
      
      const res = await authFetch('/api/fetch-meta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const data = await res.json();
      console.log('Fetch response:', data);
      
      if (data.success) {
        toast.success(data.message || `Fetched ${data.count || 0} days`, { id: tid });
        if (data.logs) console.log('API Logs:', data.logs);
        loadData();
      } else {
        // Show detailed error
        const errorMsg = data.error || data.message || 'Fetch failed';
        toast.error(errorMsg, { id: tid });
        console.error('Fetch failed:', data);
        
        // Show logs if available
        if (data.logs) {
          console.log('API Logs:', data.logs);
        }
        if (data.errors) {
          console.error('API Errors:', data.errors);
        }
      }
    } catch (e) {
      console.error('Fetch exception:', e);
      toast.error('Network error: ' + e.message, { id: tid });
    }
    
    setFetchingAccount(null);
  };

  const handleFetchAll = async () => {
    const connectedAccounts = accounts.filter(a => a.is_connected && a.access_token);
    
    if (connectedAccounts.length === 0) {
      toast.error('No connected accounts to fetch');
      return;
    }
    
    setFetchingAll(true);
    const tid = toast.loading(`Fetching ${connectedAccounts.length} account(s)...`);
    
    let successCount = 0;
    let errors = [];
    
    for (const account of connectedAccounts) {
      try {
        const accountCampaigns = campaigns.filter(c => c.meta_account_id === account.id);
        const excludedIds = accountCampaigns.filter(c => c.excluded).map(c => c.campaign_id);
        
        const res = await authFetch('/api/fetch-meta', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'fetch',
            userId: user.id,
            metaAccountId: account.id,
            accountId: account.account_id,
            accessToken: account.access_token,
            exchangeRate: account.exchange_rate || 1,
            taxRate: account.tax_rate || 0,
            excludedCampaigns: excludedIds,
          })
        });
        
        const data = await res.json();
        console.log(`Fetch ${account.account_name}:`, data);
        
        if (data.success) {
          successCount++;
        } else {
          errors.push(`${account.account_name}: ${data.error || 'Failed'}`);
        }
      } catch (e) {
        errors.push(`${account.account_name}: ${e.message}`);
      }
    }
    
    if (errors.length > 0) {
      console.error('Fetch errors:', errors);
      toast.error(`${successCount} succeeded, ${errors.length} failed. Check console.`, { id: tid });
    } else {
      toast.success(`Fetched ${successCount} account(s)`, { id: tid });
    }
    
    loadData();
    setFetchingAll(false);
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    
    if (saving) return;
    
    if (!manualForm.accountId || !manualForm.date || !manualForm.spend) {
      toast.error('Account, date and spend required');
      return;
    }
    
    setSaving(true);
    
    try {
      const account = accounts.find(a => a.id === manualForm.accountId);
      const spend = parseFloat(manualForm.spend) || 0;
      const exchangeRate = account?.exchange_rate || 1;
      const taxRate = account?.tax_rate || 0;
      // Divide by exchange rate to convert to USD (if rate > 1)
      const spendConverted = exchangeRate > 1 ? spend / exchangeRate : spend * exchangeRate;
      const spendWithTax = spendConverted * (1 + taxRate / 100);
      
      const { error } = await supabase.from('meta_daily').upsert({
        user_id: user.id,
        meta_account_id: manualForm.accountId,
        date: manualForm.date,
        spend: spendWithTax,
        spend_original: spend,
        impressions: parseInt(manualForm.impressions) || 0,
        clicks: parseInt(manualForm.clicks) || 0,
        source: 'manual',
      }, { onConflict: 'user_id,meta_account_id,date' });

      if (error) throw error;

      toast.success('Entry saved');
      setSaving(false);
      setShowManualModal(false);
      setManualForm({ accountId: '', date: new Date().toISOString().split('T')[0], spend: '', impressions: '', clicks: '' });
      await loadData();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
      setSaving(false);
    }
  };

  // Toggle campaign in campaigns modal
  const handleToggleCampaignInModal = async (campaign) => {
    try {
      await supabase
        .from('meta_campaigns')
        .update({ excluded: !campaign.excluded })
        .eq('id', campaign.id);
      
      toast.success(campaign.excluded ? 'Campaign included' : 'Campaign excluded');
      loadData();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  // Refresh campaigns for existing account
  const handleRefreshExistingCampaigns = async () => {
    if (!selectedAccount?.account_id || !selectedAccount?.access_token) {
      toast.error('API credentials missing');
      return;
    }

    setConnecting(true);
    
    try {
      const res = await authFetch('/api/fetch-meta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'connect',
          accountId: selectedAccount.account_id,
          accessToken: selectedAccount.access_token,
        })
      });
      
      const data = await res.json();
      console.log('Refresh campaigns response:', data);
      
      if (!data.success) {
        toast.error(data.error || 'Failed to refresh');
        setConnecting(false);
        return;
      }
      
      const existingCampaigns = campaigns.filter(c => c.meta_account_id === selectedAccount.id);
      const excludedMap = {};
      existingCampaigns.forEach(c => { excludedMap[c.campaign_id] = c.excluded; });
      
      const campaignRecords = (data.campaigns || []).map(c => ({
        user_id: user.id,
        meta_account_id: selectedAccount.id,
        campaign_id: c.id,
        campaign_name: c.name,
        status: c.status,
        excluded: excludedMap[c.id] || false,
      }));
      
      if (campaignRecords.length > 0) {
        await supabase.from('meta_campaigns').upsert(campaignRecords, { 
          onConflict: 'user_id,meta_account_id,campaign_id' 
        });
      }
      
      toast.success(`Refreshed! Found ${data.campaigns?.length || 0} campaign(s)`);
      loadData();
      
    } catch (e) {
      console.error('Refresh error:', e);
      toast.error('Refresh failed: ' + e.message);
    }
    
    setConnecting(false);
  };

  const openEditModal = (account) => {
    setSelectedAccount(account);
    setFormData({
      accountName: account.account_name || '',
      accountId: account.account_id || '',
      accessToken: account.access_token || '',
      currency: account.currency || 'USD',
      exchangeRate: String(account.exchange_rate || 1),
      taxRate: String(account.tax_rate || 0),
      showOnDashboard: account.show_on_dashboard !== false,
    });
    setIsConnected(account.is_connected || false);
    setConnectionError(null);
    setLoadedCampaigns([]);
    setSelectedCampaigns({});
    setShowEditModal(true);
  };

  const openCampaignsModal = (account) => {
    setSelectedAccount(account);
    setShowCampaignsModal(true);
  };

  // Stats
  const totalSpend = dailyData.reduce((s, d) => s + (Number(d.spend) || 0), 0);
  const totalImpressions = dailyData.reduce((s, d) => s + (Number(d.impressions) || 0), 0);
  const totalClicks = dailyData.reduce((s, d) => s + (Number(d.clicks) || 0), 0);
  const avgCpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  
  const monthLabel = getMonthLabel(getViewMonth());
  const connectedCount = accounts.filter(a => a.is_connected).length;
  const chartData = [...dailyData].sort((a, b) => a.date.localeCompare(b.date));
  const openManualEntryModal = () => setShowManualModal(true);

  const accountCampaigns = selectedAccount 
    ? campaigns.filter(c => c.meta_account_id === selectedAccount.id)
    : [];

  if (loading) return <SkeletonAdPlatform />;

  return (
    <div className="space-y-6">

      {/* ── 1. HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Meta Ads</h1>
          <p className="text-muted-foreground text-sm">Track Facebook &amp; Instagram ad spend</p>
          {/* Latest fetch across all connected Meta accounts. */}
          <LastUpdated
            value={accounts.reduce((latest, a) => {
              const t = a?.last_fetch || a?.updated_at;
              if (!t) return latest;
              return !latest || new Date(t) > new Date(latest) ? t : latest;
            }, null)}
            className="mt-2"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleFetchAll} disabled={fetchingAll || connectedCount === 0}>
            <Database className={`h-4 w-4 mr-2 ${fetchingAll ? 'animate-pulse' : ''}`} />
            {fetchingAll ? 'Fetching...' : `Fetch All (${connectedCount})`}
          </Button>
          <Button variant="outline" onClick={openManualEntryModal}>
            <Plus className="h-4 w-4 mr-2" />Manual Entry
          </Button>
          <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />Add Account
          </Button>
        </div>
      </div>

      {/* ── 2. KPI CARDS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total Spend', value: formatCurrency(totalSpend), cls: 'text-red-600' },
          { label: 'Impressions', value: formatNumber(totalImpressions) },
          { label: 'Clicks',      value: formatNumber(totalClicks) },
          { label: 'CTR',         value: `${avgCtr.toFixed(2)}%` },
          { label: 'Avg CPC',     value: formatCurrency(avgCpc) },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.cls || ''}`}>{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* ── 3. SPEND CHART ── */}
      <Card>
        <CardHeader><CardTitle>Spend — Last 30 Days</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v)} />
              <Tooltip formatter={v => [formatCurrency(Number(v)), 'Spend']} />
              <Line type="monotone" dataKey="spend" name="Spend" stroke="#1877f2" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ── 4. SPEND HISTORY ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle>Spend History</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setMonthOffset(monthOffset + 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[130px] text-center">{monthLabel}</span>
              <Button size="sm" variant="outline" onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} disabled={monthOffset <= 0}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={openManualEntryModal}>
                <Plus className="h-4 w-4 mr-1" />Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Impressions</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead>Src</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyData.length === 0 ? (
                    <TableEmpty colSpan={8} message={`No data for ${monthLabel}`} />
                  ) : dailyData.map(d => {
                    const ctr = d.impressions > 0 ? (d.clicks / d.impressions) * 100 : 0;
                    const cpc = d.clicks > 0 ? d.spend / d.clicks : 0;
                    return (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">{formatDate(d.date)}</TableCell>
                        <TableCell>{d.meta_accounts?.account_name || '—'}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">{formatCurrency(d.spend)}</TableCell>
                        <TableCell className="text-right">{formatNumber(d.impressions)}</TableCell>
                        <TableCell className="text-right">{formatNumber(d.clicks)}</TableCell>
                        <TableCell className="text-right">{ctr.toFixed(2)}%</TableCell>
                        <TableCell className="text-right">{formatCurrency(cpc)}</TableCell>
                        <TableCell><Badge variant={d.source === 'api' ? 'success' : 'warning'}>{d.source}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 5. AD ACCOUNTS ── */}
      <Card>
        <CardHeader>
          <CardTitle>Ad Accounts ({accounts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-40" />
              <p className="font-medium mb-1">No Meta Ads accounts yet</p>
              <p className="text-sm mb-5">Add an account to start tracking Facebook & Instagram spend</p>
              <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
                <Plus className="h-4 w-4 mr-2" />Add Account
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {accounts.map(account => {
                const accountCamps = campaigns.filter(c => c.meta_account_id === account.id);
                const excludedCount = accountCamps.filter(c => c.excluded).length;
                return (
                  <div key={account.id} className="border border-border rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      {/* Left */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${account.is_connected ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{account.account_name}</p>
                            <Badge variant={account.is_connected ? 'success' : 'secondary'}>
                              {account.is_connected ? 'Connected' : 'Manual'}
                            </Badge>
                            {account.is_connected && (
                              <Badge variant="outline">
                                {accountCamps.length - excludedCount}/{accountCamps.length} campaigns
                              </Badge>
                            )}
                            {account.show_on_dashboard && <Badge variant="outline">Overview</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {account.account_id && `ID: ${account.account_id} · `}{account.currency} · {account.tax_rate || 0}% tax
                            {account.last_fetch && ` · Synced ${formatRelativeTime(account.last_fetch)}`}
                          </p>
                        </div>
                      </div>
                      {/* Right: actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {account.is_connected && (
                          <Button size="sm" variant="outline" onClick={() => openCampaignsModal(account)} title="Campaigns">
                            <BarChart3 className="h-4 w-4 mr-1" />Campaigns
                          </Button>
                        )}
                        {account.is_connected && (
                          <Button size="sm" variant="ghost" onClick={() => handleFetchAccount(account)} disabled={fetchingAccount === account.id} title="Fetch data">
                            {fetchingAccount === account.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEditModal(account)} title="Settings">
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedAccount(account); setShowDeleteModal(true); }} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

            {/* Add Account Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); resetForm(); }}
        title={modalStep === 1 ? "Add Meta Ads Account" : "Connect API (Optional)"}
        description={modalStep === 1 ? "Enter your ad account details" : "Connect to automatically fetch spend data"}
        className="max-w-lg max-h-[90vh] flex flex-col"
      >
        {modalStep === 1 ? (
          /* Step 1: Basic Account Info */
          <form onSubmit={handleAddAccount} className="flex flex-col flex-1 overflow-hidden">
            <div className="space-y-4 overflow-y-auto flex-1 pr-2" style={{ maxHeight: 'calc(70vh - 120px)' }}>
              <Input
                label="Account Name *"
                placeholder="My Facebook Ads"
                value={formData.accountName}
                onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                required
              />
              
              <Input
                label="Ad Account ID *"
                placeholder="act_123456789"
                value={formData.accountId}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                hint="Find in Ads Manager → Settings"
                required
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Currency</label>
                  <Select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="mt-1"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                    ))}
                  </Select>
                </div>
                <Input
                  label="Exchange Rate"
                  type="number"
                  step="0.01"
                  placeholder="1"
                  value={formData.exchangeRate}
                  onChange={(e) => setFormData({ ...formData, exchangeRate: e.target.value })}
                  hint="1 USD = X of your currency"
                />
              </div>
              
              <Input
                label="Tax Rate (%)"
                type="number"
                step="0.01"
                placeholder="0"
                value={formData.taxRate}
                onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                hint="VAT added to ad spend"
              />
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showOnDashboard"
                  checked={formData.showOnDashboard}
                  onChange={(e) => setFormData({ ...formData, showOnDashboard: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="showOnDashboard" className="cursor-pointer">
                  Show on Overview ROAS calculation
                </label>
              </div>
            </div>
            
            <ModalFooter>
              <Button variant="outline" type="button" onClick={() => { setShowAddModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" loading={saving}>
                Continue
              </Button>
            </ModalFooter>
          </form>
        ) : (
          /* Step 2: Optional API Connection */
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="space-y-4 overflow-y-auto flex-1 pr-2" style={{ maxHeight: 'calc(70vh - 120px)' }}>
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Account Created!</p>
                    <p className="text-sm text-green-600">
                      You can now optionally connect the API to automatically fetch spend data.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4 p-4 border rounded-lg bg-blue-50">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Get your Access Token from{' '}
                    <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="underline">
                      Meta Graph API Explorer
                    </a>. Required permissions: <code>ads_read</code>
                  </AlertDescription>
                </Alert>
                
                <Input
                  label="Access Token"
                  type="password"
                  placeholder="EAAxxxxxx..."
                  value={formData.accessToken}
                  onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                  hint="Use a long-lived token for best results"
                  disabled={isConnected}
                />
                
                {connectionError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{connectionError}</AlertDescription>
                  </Alert>
                )}
                
                {!isConnected ? (
                  <Button 
                    type="button" 
                    onClick={handleConnect} 
                    disabled={connecting || !formData.accessToken}
                    className="w-full"
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <LogIn className="h-4 w-4 mr-2" />
                        Connect & Load Campaigns
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="success" className="gap-1">
                        <Check className="h-3 w-3" /> Connected
                      </Badge>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setIsConnected(false);
                          setLoadedCampaigns([]);
                          setSelectedCampaigns({});
                        }}
                      >
                        <X className="h-4 w-4 mr-1" /> Disconnect
                      </Button>
                    </div>
                    
                    {loadedCampaigns.length > 0 ? (
                      <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                        <div className="p-2 bg-gray-100 text-sm font-medium sticky top-0">
                          Select Campaigns ({Object.values(selectedCampaigns).filter(Boolean).length}/{loadedCampaigns.length} included)
                        </div>
                        {loadedCampaigns.map(campaign => (
                          <div 
                            key={campaign.id}
                            className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => toggleCampaign(campaign.id)}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selectedCampaigns[campaign.id] 
                                ? 'bg-green-500 border-green-500' 
                                : 'bg-gray-200 border-gray-300'
                            }`}>
                              {selectedCampaigns[campaign.id] && <Check className="h-3 w-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${!selectedCampaigns[campaign.id] ? 'text-muted-foreground line-through' : ''}`}>
                                {campaign.name}
                              </p>
                              <p className="text-xs text-muted-foreground">{campaign.status}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No campaigns found in this account
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <ModalFooter>
              <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
                {isConnected ? 'Close' : 'Skip for Now'}
              </Button>
              {isConnected && (
                <Button onClick={handleConnectApi} loading={saving}>
                  Save API Connection
                </Button>
              )}
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* Edit Account Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedAccount(null); resetForm(); }}
        title="Edit Meta Ads Account"
        description="Update account settings"
        className="max-w-lg max-h-[90vh] flex flex-col"
      >
        <form onSubmit={handleEditAccount} className="flex flex-col flex-1 overflow-hidden">
          <div className="space-y-4 overflow-y-auto flex-1 pr-2" style={{ maxHeight: 'calc(70vh - 120px)' }}>
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Account Details</h4>
              
              <Input
                label="Account Name *"
                placeholder="My Facebook Ads"
                value={formData.accountName}
                onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                required
              />
              
              <Input
                label="Ad Account ID *"
                placeholder="act_123456789"
                value={formData.accountId}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                required
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Currency</label>
                  <Select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="mt-1"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                  </Select>
                </div>
                <Input
                  label="Exchange Rate"
                  type="number"
                  step="0.01"
                  placeholder="1"
                  value={formData.exchangeRate}
                  onChange={(e) => setFormData({ ...formData, exchangeRate: e.target.value })}
                  hint="1 USD = X of your currency"
                />
              </div>
              
              <Input
                label="Tax Rate (%)"
                type="number"
                step="0.01"
                value={formData.taxRate}
                onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                hint="VAT added to ad spend"
              />
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showOnDashboardEdit"
                  checked={formData.showOnDashboard}
                  onChange={(e) => setFormData({ ...formData, showOnDashboard: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="showOnDashboardEdit" className="cursor-pointer">
                  Show on Overview ROAS calculation
                </label>
              </div>
            </div>
            
            <hr />
            
            {/* API Connection Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">API Connection (Optional)</h4>
              
              <Input
                label="Access Token"
                type="password"
                placeholder="EAAxxxxxx..."
                value={formData.accessToken}
                onChange={(e) => {
                  setFormData({ ...formData, accessToken: e.target.value });
                  if (e.target.value !== selectedAccount?.access_token) {
                    setIsConnected(false);
                  }
                }}
                hint="Leave empty to use manual data entry"
              />
              
              {connectionError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{connectionError}</AlertDescription>
                </Alert>
              )}
              
              {formData.accessToken && !isConnected && formData.accessToken !== selectedAccount?.access_token && (
                <Button 
                  type="button" 
                  onClick={handleConnect} 
                  disabled={connecting}
                  className="w-full"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <LogIn className="h-4 w-4 mr-2" />
                      Verify & Load Campaigns
                    </>
                  )}
                </Button>
              )}
              
              {selectedAccount?.is_connected && (
                <Badge variant="success" className="gap-1">
                  <Check className="h-3 w-3" /> API Connected
                </Badge>
              )}
              
              {isConnected && loadedCampaigns.length > 0 && (
                <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                  <div className="p-2 bg-gray-100 text-sm font-medium sticky top-0 flex justify-between">
                    <span>Campaigns ({Object.values(selectedCampaigns).filter(Boolean).length}/{loadedCampaigns.length})</span>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="ghost"
                      onClick={handleConnect}
                      disabled={connecting}
                    >
                      <RefreshCw className={`h-3 w-3 ${connecting ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  {loadedCampaigns.map(campaign => (
                    <div 
                      key={campaign.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => toggleCampaign(campaign.id)}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedCampaigns[campaign.id] 
                          ? 'bg-green-500 border-green-500' 
                          : 'bg-gray-200 border-gray-300'
                      }`}>
                        {selectedCampaigns[campaign.id] && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span className={`text-sm truncate ${!selectedCampaigns[campaign.id] ? 'text-muted-foreground line-through' : ''}`}>
                        {campaign.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => { setShowEditModal(false); setSelectedAccount(null); resetForm(); }}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Manual Entry Modal */}
      <Modal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        title="Manual Entry"
        description="Add spend data manually"
      >
        <form onSubmit={handleManualEntry}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Account *</label>
              <Select
                value={manualForm.accountId}
                onChange={(e) => setManualForm({ ...manualForm, accountId: e.target.value })}
                className="mt-1"
                required
              >
                <option value="">Select account...</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.account_name}</option>
                ))}
              </Select>
            </div>
            <Input
              label="Date *"
              type="date"
              value={manualForm.date}
              onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
              required
            />
            <Input
              label="Spend Amount *"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={manualForm.spend}
              onChange={(e) => setManualForm({ ...manualForm, spend: e.target.value })}
              hint="In account's original currency"
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Impressions"
                type="number"
                placeholder="0"
                value={manualForm.impressions}
                onChange={(e) => setManualForm({ ...manualForm, impressions: e.target.value })}
              />
              <Input
                label="Clicks"
                type="number"
                placeholder="0"
                value={manualForm.clicks}
                onChange={(e) => setManualForm({ ...manualForm, clicks: e.target.value })}
              />
            </div>
          </div>
          
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => setShowManualModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              Save Entry
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Campaigns Modal */}
      <Modal
        isOpen={showCampaignsModal}
        onClose={() => { setShowCampaignsModal(false); setSelectedAccount(null); }}
        title={`Campaigns - ${selectedAccount?.account_name}`}
        description="Select campaigns to include in reporting"
      >
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {accountCampaigns.filter(c => !c.excluded).length} of {accountCampaigns.length} included
            </p>
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleRefreshExistingCampaigns}
              disabled={connecting}
            >
              {connecting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
          </div>
          
          <div className="border rounded-lg max-h-[400px] overflow-y-auto">
            {accountCampaigns.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No campaigns found. Click Refresh to load.
              </p>
            ) : (
              accountCampaigns.map(campaign => (
                <div 
                  key={campaign.id}
                  className={`flex items-center justify-between p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${campaign.excluded ? 'opacity-60' : ''}`}
                  onClick={() => handleToggleCampaignInModal(campaign)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      !campaign.excluded 
                        ? 'bg-green-500 border-green-500' 
                        : 'bg-gray-200 border-gray-300'
                    }`}>
                      {!campaign.excluded && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <p className={`font-medium ${campaign.excluded ? 'line-through text-muted-foreground' : ''}`}>
                        {campaign.campaign_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ID: {campaign.campaign_id} • {campaign.status}
                      </p>
                    </div>
                  </div>
                  <Badge variant={campaign.excluded ? 'secondary' : 'success'}>
                    {campaign.excluded ? 'Excluded' : 'Included'}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </div>
        
        <ModalFooter>
          <Button variant="outline" onClick={() => { setShowCampaignsModal(false); setSelectedAccount(null); }}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Debug Modal */}
      <Modal
        isOpen={showDebugModal}
        onClose={() => { setShowDebugModal(false); setDebugData(null); }}
        title={`Debug - ${selectedAccount?.account_name}`}
        description="API connection test results"
      >
        <div className="space-y-4">
          {debugLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : debugData ? (
            <div className="space-y-4">
              {debugData.error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{debugData.error}</AlertDescription>
                </Alert>
              )}
              
              {debugData.debug && (
                <>
                  <div>
                    <p className="text-sm font-medium mb-1">Account ID Used:</p>
                    <code className="text-xs bg-gray-100 p-2 rounded block">
                      {debugData.debug.actId}
                    </code>
                  </div>
                  
                  {debugData.debug.tests?.accountInfo && (
                    <div>
                      <p className="text-sm font-medium mb-1">Account Info:</p>
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-[150px]">
                        {JSON.stringify(debugData.debug.tests.accountInfo.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {debugData.debug.tests?.campaigns && (
                    <div>
                      <p className="text-sm font-medium mb-1">
                        Campaigns ({debugData.debug.tests.campaigns.count}):
                      </p>
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-[150px]">
                        {JSON.stringify(debugData.debug.tests.campaigns.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {debugData.debug.tests?.insights && (
                    <div>
                      <p className="text-sm font-medium mb-1">
                        Insights ({debugData.debug.tests.insights.date}):
                      </p>
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-[150px]">
                        {JSON.stringify(debugData.debug.tests.insights.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {debugData.debug.tests?.tokenInfo && (
                    <div>
                      <p className="text-sm font-medium mb-1">Token Info:</p>
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-[150px]">
                        {JSON.stringify(debugData.debug.tests.tokenInfo, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No data</p>
          )}
        </div>
        
        <ModalFooter>
          <Button variant="outline" onClick={() => { setShowDebugModal(false); setDebugData(null); }}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedAccount(null); }}
        title="Delete Account"
        description="Are you sure?"
      >
        <Alert variant="warning">
          <AlertDescription>
            This will delete <strong>{selectedAccount?.account_name}</strong> and all associated data. This cannot be undone.
          </AlertDescription>
        </Alert>
        <ModalFooter>
          <Button variant="outline" onClick={() => { setShowDeleteModal(false); setSelectedAccount(null); }}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDeleteAccount} loading={saving}>
            Delete Account
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  );
}
